import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type BreadcrumbState = {
  organizationId: string | null;
  label: string | null;
};

type CustomerDetailBreadcrumbContextValue = {
  organizationId: string | null;
  label: string | null;
  setBreadcrumb: (organizationId: string, label: string) => void;
  clearBreadcrumb: () => void;
};

const CustomerDetailBreadcrumbContext =
  createContext<CustomerDetailBreadcrumbContextValue | null>(null);

export function CustomerDetailBreadcrumbProvider({
  children,
}: Readonly<{ children: ReactNode }>) {
  const [state, setState] = useState<BreadcrumbState>({
    organizationId: null,
    label: null,
  });

  const setBreadcrumb = useCallback((organizationId: string, label: string) => {
    setState({ organizationId, label });
  }, []);

  const clearBreadcrumb = useCallback(() => {
    setState({ organizationId: null, label: null });
  }, []);

  const value = useMemo(
    () => ({
      organizationId: state.organizationId,
      label: state.label,
      setBreadcrumb,
      clearBreadcrumb,
    }),
    [state.organizationId, state.label, setBreadcrumb, clearBreadcrumb],
  );

  return (
    <CustomerDetailBreadcrumbContext.Provider value={value}>
      {children}
    </CustomerDetailBreadcrumbContext.Provider>
  );
}

export function useCustomerDetailBreadcrumb(): CustomerDetailBreadcrumbContextValue {
  const ctx = useContext(CustomerDetailBreadcrumbContext);
  if (!ctx) {
    throw new Error(
      "useCustomerDetailBreadcrumb must be used within CustomerDetailBreadcrumbProvider",
    );
  }
  return ctx;
}
